import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  VStack,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { GetStationSummary } from 'reactRedux/thunks/stationThunk/stationThunk';
import {
  FiWifi,
  FiWifiOff,
  FiClock,
  FiUsers,
  FiInfo,
  FiAlertTriangle,
} from 'react-icons/fi';

const StationSummaryCard = () => {
  const dispatch = useDispatch();
  const { stationSummaryData, isSummaryLoading } = useSelector(
    (state) => state.StationSlice,
  );

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    dispatch(GetStationSummary());
  }, [dispatch]);

  if (isSummaryLoading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner size="lg" color="blue.500" />
      </Flex>
    );
  }

  if (!stationSummaryData) {
    return null;
  }

  const {
    onlineStation,
    offlineStation,
    offlineStationInWorkingHours,
    sellerOfflineStations,
  } = stationSummaryData;

  const dataHide = false;

  return (
    <Box mb={6}>
      {/* Summary Cards Row */}
      <Flex gap={4} mb={6} flexWrap="wrap">
        {/* Total Stations */}
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          // flex="1"
          minW="350px"
          boxShadow="sm"
        >
          <CardBody>
            <Flex align="center" justify="space-between">
              <VStack align="start" spacing={0}>
                <Text
                  fontSize="sm"
                  color={mutedColor}
                  fontWeight="500"
                  textTransform="uppercase"
                >
                  Offline Stations
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {onlineStation} / {offlineStation}
                  <Divider orientation="vertical" mx={2} />
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                  >
                    <FiAlertTriangle size={14} color="red" />
                    <Text as="span" fontSize="sm" color={mutedColor}>
                      {offlineStationInWorkingHours} offline
                    </Text>
                    <Tooltip
                      label="The station is currently offline during merchant opening hours. Stations offline due to maintenance mode are not included."
                      placement="top"
                      hasArrow
                    >
                      <span
                        style={{
                          marginLeft: '4px',
                          cursor: 'help',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <FiInfo size={14} color="gray" />
                      </span>
                    </Tooltip>
                  </Box>
                </Text>
              </VStack>
              {/* <Box p={3} borderRadius="lg" bg="blue.50" color="blue.500">
                <FiWifi size={24} />
              </Box> */}
            </Flex>
          </CardBody>
        </Card>

        {/* Online Stations */}
        {dataHide && (
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            flex="1"
            minW="200px"
            boxShadow="sm"
          >
            <CardBody>
              <Flex align="center" justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize="sm"
                    color={mutedColor}
                    fontWeight="500"
                    textTransform="uppercase"
                  >
                    Online Stations
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    {onlineStation || 0}
                  </Text>
                </VStack>
                <Box p={3} borderRadius="lg" bg="green.50" color="green.500">
                  <FiWifi size={24} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Offline Stations */}
        {dataHide && (
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            flex="1"
            minW="200px"
            boxShadow="sm"
          >
            <CardBody>
              <Flex align="center" justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize="sm"
                    color={mutedColor}
                    fontWeight="500"
                    textTransform="uppercase"
                  >
                    Offline Stations
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="red.500">
                    {offlineStation || 0}
                  </Text>
                </VStack>
                <Box p={3} borderRadius="lg" bg="red.50" color="red.500">
                  <FiWifiOff size={24} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Offline in Working Hours */}
        {dataHide && (
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            flex="1"
            minW="200px"
            boxShadow="sm"
          >
            <CardBody>
              <Flex align="center" justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize="sm"
                    color={mutedColor}
                    fontWeight="500"
                    textTransform="uppercase"
                  >
                    Offline in Working Hours
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                    {offlineStationInWorkingHours || 0}
                  </Text>
                </VStack>
                <Box p={3} borderRadius="lg" bg="orange.50" color="orange.500">
                  <FiClock size={24} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        )}
      </Flex>

      {/* Offline Stations List */}
      {sellerOfflineStations && sellerOfflineStations.length > 0 && (
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
        >
          <CardBody p={0}>
            <Box p={4} pb={2}>
              <Flex align="center" gap={2}>
                <FiUsers color="orange" />
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Sellers with offline stations
                </Text>
                {/* <Badge colorScheme="orange" ml={2}>
                  {sellerOfflineStations.length}
                </Badge> */}
              </Flex>
            </Box>
            <Divider />
            <Box p={4}>
              {(() => {
                // Group sellers by sellerId and aggregate their offline stations
                const sellerMap = new Map();

                sellerOfflineStations.forEach((seller) => {
                  const sellerId = seller.sellerId || 'N/A';
                  const sellerName =
                    seller.sellerFirstName && seller.sellerLastName
                      ? `${seller.sellerFirstName} ${seller.sellerLastName}`
                      : seller.sellerFirstName ||
                        seller.sellerLastName ||
                        'N/A';

                  if (sellerMap.has(sellerId)) {
                    // Add to existing seller's count
                    sellerMap.get(sellerId).count +=
                      seller.offlineStations.length;
                  } else {
                    // Create new entry
                    sellerMap.set(sellerId, {
                      name: sellerName,
                      count: seller.offlineStations.length,
                    });
                  }
                });

                // Convert to array and calculate totals
                const uniqueSellers = Array.from(sellerMap.values());

                // Sort sellers: N/A goes last, others alphabetically
                uniqueSellers.sort((a, b) => {
                  if (a.name === 'N/A') return 1;
                  if (b.name === 'N/A') return -1;
                  return a.name.localeCompare(b.name);
                });

                return (
                  <>
                    <Flex gap={2} flexWrap="wrap">
                      {uniqueSellers.map((seller, index) => (
                        <Badge
                          key={index}
                          colorScheme="red"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="sm"
                          fontWeight="500"
                        >
                          {seller.name}: {seller.count}{' '}
                          {seller.count === 1 ? 'station' : 'stations'}
                        </Badge>
                      ))}
                    </Flex>
                  </>
                );
              })()}
            </Box>

            {/* <Box overflowX="auto" maxH="400px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg={cardBg} zIndex={1}>
                  <Tr>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Station ID
                    </Th>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Merchant
                    </Th>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Status
                    </Th>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Working Hours
                    </Th>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Seller
                    </Th>
                    <Th
                      bg={cardBg}
                      color={mutedColor}
                      fontSize="xs"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                    >
                      Session ID
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sellerOfflineStations.map((seller) =>
                    seller.offlineStations.map((station, stationIndex) => (
                      <Tr
                        key={`${seller.merchantId}-${station.stationId}-${stationIndex}`}
                        _hover={{ bg: 'gray.50' }}
                      >
                        <Td>
                          <Link
                            to={`/admin/stations/details/${station.stationId}`}
                          >
                            <Text
                              color="blue.500"
                              fontWeight="600"
                              fontSize="sm"
                              _hover={{ textDecoration: 'underline' }}
                              whiteSpace="nowrap"
                            >
                              {station.stationId}
                            </Text>
                          </Link>
                        </Td>
                        <Td>
                          <Tooltip
                            label={seller.merchantName}
                            placement="top"
                            hasArrow
                          >
                            <Text
                              color={textColor}
                              fontWeight="500"
                              fontSize="sm"
                              maxW="180px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {seller.merchantName}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={station.status === 2 ? 'red' : 'green'}
                            fontSize="xs"
                          >
                            {station.status === 2 ? 'Offline' : 'Online'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              station.isInWorkingHours ? 'orange' : 'gray'
                            }
                            fontSize="xs"
                          >
                            {station.isInWorkingHours ? 'Yes' : 'No'}
                          </Badge>
                        </Td>
                        <Td>
                          {seller.sellerFirstName || seller.sellerLastName ? (
                            <HStack spacing={1}>
                              <Text
                                color={textColor}
                                fontWeight="500"
                                fontSize="sm"
                              >
                                {seller.sellerFirstName} {seller.sellerLastName}
                              </Text>
                            </HStack>
                          ) : (
                            <Text
                              color={mutedColor}
                              fontSize="sm"
                              fontStyle="italic"
                            >
                              Unassigned
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Tooltip
                            label={seller.sellerId || 'N/A'}
                            placement="top"
                            hasArrow
                          >
                            <Text
                              color={textColor}
                              fontSize="sm"
                              maxW="80px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {seller.sellerId || '-'}
                            </Text>
                          </Tooltip>
                        </Td>
                      </Tr>
                    )),
                  )}
                </Tbody>
              </Table>
            </Box> */}
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default StationSummaryCard;
